import { createElement, useState } from "react";
import { router } from "@inertiajs/react";
import { ArrowLeftIcon, CheckCircleIcon, BanknotesIcon } from "@heroicons/react/24/outline";
import { AdminLayout } from "../../../layouts/admin-layout";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@components/ui/dialog";

interface Payout {
  id: number;
  partner_name: string;
  partner_slug: string;
  partner_id: number;
  period: string;
  commission_cents: number;
  revenue_cents: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  prev_page: number | null;
  next_page: number | null;
}

interface PayoutsProps {
  payouts: Payout[];
  pagination: Pagination;
  filter: string;
}

function formatCents(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) {
    const m = dollars / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (dollars >= 10_000) {
    const k = dollars / 1_000;
    return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return `$${dollars.toFixed(2)}`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatPeriod(period: string): string {
  const [year, month] = period.split("-");
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    completed: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100 text-gray-500"}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function PaginationControls({ pagination }: { pagination: Pagination }) {
  if (pagination.total_pages <= 1) return null;

  const navigate = (page: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(page));
    router.visit(url.toString(), { preserveState: true, preserveScroll: true });
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
      <span className="text-sm text-muted-foreground">
        Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_count} total)
      </span>
      <div className="flex gap-2">
        <button
          onClick={() => navigate(pagination.current_page - 1)}
          disabled={!pagination.prev_page}
          className="px-3 py-1 text-sm rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => navigate(pagination.current_page + 1)}
          disabled={!pagination.next_page}
          className="px-3 py-1 text-sm rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function Payouts({ payouts, pagination, filter }: PayoutsProps) {
  const [confirmingPayout, setConfirmingPayout] = useState<Payout | null>(null);
  const [stripeTransferId, setStripeTransferId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleMarkPaid = () => {
    if (!confirmingPayout || !stripeTransferId.trim()) return;
    setSubmitting(true);
    router.patch(`/admin/partner_payouts/${confirmingPayout.id}/mark_paid`, {
      stripe_transfer_id: stripeTransferId.trim(),
    }, {
      onFinish: () => {
        setSubmitting(false);
        setConfirmingPayout(null);
        setStripeTransferId("");
      },
    });
  };

  const setFilter = (newFilter: string) => {
    router.visit(`/admin/partner_payouts?filter=${newFilter}`, {
      preserveState: true,
      preserveScroll: true,
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={() => router.visit("/admin/partners")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-3xl font-bold text-foreground">Partner Payouts</h1>
          </div>
          <p className="text-muted-foreground mt-1 ml-8">
            Review and process partner commission payouts
          </p>
        </div>
      </header>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-6 border-b border-border">
        {["pending", "completed", "failed"].map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              filter === tab
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Payouts Table */}
      <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Partner</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Period</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Revenue</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Commission</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Created</th>
                {filter === "pending" && (
                  <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payouts.map((payout) => (
                <tr key={payout.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => router.visit(`/admin/partners/${payout.partner_id}`)}
                      className="font-medium text-foreground hover:text-primary transition-colors"
                    >
                      {payout.partner_name}
                    </button>
                    <div className="text-xs text-muted-foreground font-mono">{payout.partner_slug}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{formatPeriod(payout.period)}</td>
                  <td className="px-4 py-3 text-right text-sm text-foreground">{formatCents(payout.revenue_cents)}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-foreground">{formatCents(payout.commission_cents)}</td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={payout.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(payout.created_at)}</td>
                  {filter === "pending" && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setConfirmingPayout(payout)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        Mark Paid
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {payouts.length === 0 && (
                <tr>
                  <td
                    colSpan={filter === "pending" ? 7 : 6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    No {filter} payouts.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls pagination={pagination} />
      </div>

      {/* Confirmation Modal */}
      <Dialog open={!!confirmingPayout} onOpenChange={(open) => { if (!open) { setConfirmingPayout(null); setStripeTransferId(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Payout</DialogTitle>
            <DialogDescription>
              Mark this payout as paid? This confirms that the commission has been transferred to the partner.
            </DialogDescription>
          </DialogHeader>
          {confirmingPayout && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Partner</span>
                  <span className="text-sm font-medium">{confirmingPayout.partner_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Period</span>
                  <span className="text-sm font-medium">{formatPeriod(confirmingPayout.period)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Commission</span>
                  <span className="text-sm font-bold text-green-600">{formatCents(confirmingPayout.commission_cents)}</span>
                </div>
              </div>
              <div>
                <label htmlFor="stripe_transfer_id" className="block text-sm font-medium text-foreground mb-1">
                  Stripe Transfer ID
                </label>
                <input
                  id="stripe_transfer_id"
                  type="text"
                  value={stripeTransferId}
                  onChange={(e) => setStripeTransferId(e.target.value)}
                  placeholder="tr_..."
                  className="w-full px-3 py-2 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary font-mono"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Enter the Stripe transfer or payout ID as proof of payment.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <button className="px-4 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted transition-colors">
                Cancel
              </button>
            </DialogClose>
            <button
              onClick={handleMarkPaid}
              disabled={submitting || !stripeTransferId.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BanknotesIcon className="w-4 h-4" />
              {submitting ? "Processing..." : "Confirm Paid"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

Payouts.layout = (page: React.ReactNode) =>
  createElement(AdminLayout, null, page);

export default Payouts;
